import {Collapse, IconButton, Stack, TableCell, TableRow, Typography} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {getDateTime} from '@mybake/ui/common/helpers';
import {getStyles} from '../../recipes/recipeCommon';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {OrdersGroupByDayGuidDto} from '@mybake/ui/models/Order/IOrderGuid';
import OrderCardGuid from './OrderCardGuid';
import {getOrderDateKey, getTodayDateKey} from './orderDisplay';

interface Props {
    ordersByDay: OrdersGroupByDayGuidDto;
    startsNewWeek: boolean;
    separatesDay: boolean;
    alternateWeek: boolean;
    isOpen: boolean;
    openOrderId: string | null;
    onToggleDate: (date: string) => void;
    onToggleOrder: (orderId: string) => void;
    getEditPath?: (orderId: string) => string;
    editReturnPath?: string;
}

export const OrdersByDayGuid = ({ordersByDay, startsNewWeek, separatesDay, alternateWeek, isOpen, openOrderId, onToggleDate, onToggleOrder, getEditPath, editReturnPath}: Props) => {
    const {t} = useAppTranslation();
    const {tableRowWrapper} = getStyles();
    const dayNames: Record<string, string> = {
        sunday: t('dates:sunday'), monday: t('dates:monday'), tuesday: t('dates:tuesday'),
        wednesday: t('dates:wednesday'), thursday: t('dates:thursday'), friday: t('dates:friday'), saturday: t('dates:saturday'),
    };
    const isToday = getOrderDateKey(ordersByDay.date) === getTodayDateKey();
    const contentId = `orders-for-${getOrderDateKey(ordersByDay.date)}`;
    const background = alternateWeek ? 'rgba(154, 90, 60, 0.075)' : 'rgba(141, 170, 145, 0.055)';
    return <>
        <TableRow role="button" tabIndex={0} aria-expanded={isOpen} aria-controls={contentId}
                  onClick={() => onToggleDate(ordersByDay.date)}
                  onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onToggleDate(ordersByDay.date); } }}
                  sx={{...tableRowWrapper, cursor: 'pointer', backgroundColor: isToday ? 'rgba(154, 90, 60, 0.15)' : background}}>
            <TableCell sx={{py: 1.25, px: {xs: 0.75, sm: 2}, borderBottom: '1px solid', borderBottomColor: 'divider', ...(startsNewWeek ? {borderTop: '3px solid', borderTopColor: alternateWeek ? 'primary.light' : 'secondary.light'} : {})}}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" alignItems="center" minWidth={0}>
                        <IconButton size="small" tabIndex={-1}>{isOpen ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}</IconButton>
                        <Typography fontWeight={600} noWrap>
                            {isToday && <Typography component="span" color="primary.main" fontWeight={700}>{t('orders:today')} · </Typography>}
                            {getDateTime(ordersByDay.date)?.date || ordersByDay.date} · {dayNames[ordersByDay.week.day.toLowerCase()]}
                        </Typography>
                    </Stack>
                    <Typography variant="caption" sx={{px: .75, py: .35, borderRadius: 5, bgcolor: 'rgba(154, 90, 60, 0.09)', fontWeight: 700}}>
                        {t('orders:orders_count', {count: ordersByDay.orders.length})}
                    </Typography>
                </Stack>
            </TableCell>
        </TableRow>
        <TableRow id={contentId}><TableCell sx={{p: 0, border: 0}}>
            <Collapse in={isOpen} timeout="auto" unmountOnExit sx={separatesDay ? {borderTop: '1px solid', borderTopColor: 'divider'} : undefined}>
                <Stack spacing={.75} sx={{p: {xs: .75, sm: 1.25}}}>
                    {ordersByDay.orders.map(order => <OrderCardGuid key={order.id} order={order} expanded={openOrderId === order.id} onToggle={onToggleOrder} editPath={getEditPath?.(order.id)} editReturnPath={editReturnPath}/>)}
                </Stack>
            </Collapse>
        </TableCell></TableRow>
    </>;
};
